import { type CustomWorkflowSummary } from '../../../store/customWorkflow/customWorkflows'
import {
  parseWorkflowTags,
  WORKFLOW_CATEGORIES,
  WORKFLOW_CATEGORY_KEYS,
  WORKFLOW_KINDS,
  type WorkflowCategoryKey,
  type WorkflowKind,
} from '../../../components/CustomWorkflows/workflowTags'

export interface LibraryFilter {
  kind: WorkflowKind | 'all'
  category: WorkflowCategoryKey | 'all'
  tag: string | null
}

export const NO_LIBRARY_FILTER: LibraryFilter = {
  kind: 'all',
  category: 'all',
  tag: null,
}

export interface KindOption {
  key: WorkflowKind | 'all'
  label: string
  count: number
}

export interface CategoryOption {
  key: WorkflowCategoryKey | 'all'
  label: string
  icon: string
  count: number
}

export interface QuickTag {
  tag: string
  count: number
}

export interface LibraryCatalog {
  manual: CustomWorkflowSummary[]
  triggered: CustomWorkflowSummary[]
  hooks: CustomWorkflowSummary[]
  manualTotal: number
  triggeredTotal: number
  hooksTotal: number
  kinds: KindOption[]
  categories: CategoryOption[]
  quickTags: QuickTag[]
  selected: LibraryFilter
}

const KIND_LABELS: Record<WorkflowKind, string> = {
  workflow: 'Workflows',
  agent: 'Agents',
}

const QUICK_TAG_LIMIT = 12

const byName = (a: CustomWorkflowSummary, b: CustomWorkflowSummary): number => (a.name || a.id).localeCompare(b.name || b.id)

const countBy = <T>(values: T[]): Map<T, number> => values.reduce((counts, value) => counts.set(value, (counts.get(value) ?? 0) + 1), new Map<T, number>())

const matches = (workflow: CustomWorkflowSummary, filter: LibraryFilter): boolean => {
  const parsed = parseWorkflowTags(workflow)
  if (filter.kind !== 'all' && parsed.kind !== filter.kind) {
    return false
  }
  if (filter.category !== 'all' && parsed.category !== filter.category) {
    return false
  }
  return filter.tag === null || parsed.hashtags.includes(filter.tag)
}

const effectiveFilter = (workflows: CustomWorkflowSummary[], filter: LibraryFilter): LibraryFilter => {
  const parsed = workflows.map(parseWorkflowTags)
  const kind = filter.kind !== 'all' && !parsed.some((tags) => tags.kind === filter.kind) ? 'all' : filter.kind
  const category = filter.category !== 'all' && !parsed.some((tags) => tags.category === filter.category) ? 'all' : filter.category
  const tag = filter.tag !== null && !parsed.some((tags) => tags.hashtags.includes(filter.tag ?? '')) ? null : filter.tag
  return { kind, category, tag }
}

const kindOptions = (workflows: CustomWorkflowSummary[]): KindOption[] => {
  const counts = countBy(workflows.map((workflow) => parseWorkflowTags(workflow).kind))
  return [
    { key: 'all', label: 'All types', count: workflows.length },
    ...WORKFLOW_KINDS.flatMap((kind) => {
      const count = counts.get(kind)
      return count === undefined ? [] : [{ key: kind, label: KIND_LABELS[kind], count }]
    }),
  ]
}

const categoryOptions = (workflows: CustomWorkflowSummary[]): CategoryOption[] => {
  const counts = countBy(workflows.map((workflow) => parseWorkflowTags(workflow).category))
  return [
    { key: 'all', label: 'All', icon: 'bi-grid', count: workflows.length },
    ...WORKFLOW_CATEGORY_KEYS.flatMap((key) => {
      const count = counts.get(key)
      return count === undefined
        ? []
        : [
            {
              key,
              label: WORKFLOW_CATEGORIES[key].label,
              icon: WORKFLOW_CATEGORIES[key].icon,
              count,
            },
          ]
    }),
  ]
}

const quickTags = (workflows: CustomWorkflowSummary[]): QuickTag[] => {
  const counts = countBy(workflows.flatMap((workflow) => parseWorkflowTags(workflow).hashtags))
  return [...counts.entries()]
    .map(([tag, count]) => ({ tag, count }))
    .sort((a, b) => b.count - a.count || a.tag.localeCompare(b.tag))
    .slice(0, QUICK_TAG_LIMIT)
}

export const buildCatalog = (workflows: CustomWorkflowSummary[], filter: LibraryFilter): LibraryCatalog => {
  const selected = effectiveFilter(workflows, filter)
  const manualAll = workflows.filter((workflow) => workflow.triggerType === 'manual')
  const triggeredAll = workflows.filter((workflow) => workflow.triggerType === 'event')
  const hooksAll = workflows.filter((workflow) => workflow.triggerType === 'hook')
  return {
    manual: manualAll.filter((workflow) => matches(workflow, selected)).sort(byName),
    triggered: triggeredAll.filter((workflow) => matches(workflow, selected)).sort(byName),
    hooks: hooksAll.filter((workflow) => matches(workflow, selected)).sort(byName),
    manualTotal: manualAll.length,
    triggeredTotal: triggeredAll.length,
    hooksTotal: hooksAll.length,
    kinds: kindOptions(workflows),
    categories: categoryOptions(workflows),
    quickTags: quickTags(workflows),
    selected,
  }
}
